<template>
  <RouterView v-if="FullLoaded" v-slot="{ Component }">
    <div class="app-content">
      <component :is="Component" />
      <div id="modal-popup"></div>
      <AppUIToast />
      <AppUIConfirm />
      <AppUICameraCrashNotice />
      <AppUILoading
        :isLoading="isLoading || isUniqueLoading"
        :loadingPercent="loadingPercent"
        :loadingMessage="loadingMessage"
      />
      <AppUILoadingTask :isLoading="isTaskLoading" />
      <div v-if="!IS_PRODUCTION" class="dev-watermark">
        <div class="dev-watermark-large">Zentrix 개발 버전</div>
        <div>개발 버전은 데이터가 영구저장되지 않습니다.</div>
        <div class="dev-route">{{ route.fullPath }}</div>
      </div>
      <!-- 스와이프 내비게이션 인디케이터 -->
      <div
        v-if="isSwipeActive"
        class="swipe-nav-indicator"
        :class="swipeDirection === 'forward' ? 'swipe-nav-right' : 'swipe-nav-left'"
        :style="{
          opacity: swipeProgress,
          transform: `translateY(-50%) scale(${0.6 + swipeProgress * 0.4})`,
        }"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path
            :d="swipeDirection === 'forward' ? 'M9 6l6 6-6 6' : 'M15 18l-6-6 6-6'"
            stroke="white"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
  </RouterView>
</template>

<script setup lang="ts">
  import { RouterView, useRoute } from 'vue-router'
  import { ref, watch, onMounted, computed } from 'vue'
  import { AppState, App as CapacitorApp } from '@capacitor/app'
  import { App } from './app'
  import { isApp, getCodepushInstance, IS_PRODUCTION } from '@/index'
  import { PUBLIC_PATHS } from '@/app/constant'
  import { initializeSQLiteDatabase, SQL } from '@/lib/Sqlite'
  import { Logger } from '@homecheck/logger'
  import { useAppStateStore } from '@/app/store/state'
  import { checkAndRequestAllPermissions } from '@/lib/Permissions'
  import { useAllTaskStore } from '@/app/store/task'
  import { UpdateEvent } from './lib/Codepush/type'
  import { checkRequiredNativeUpdate, initBackButtonListener } from './app/util'
  import { useUserStore } from '@/app/store/user'
  import { storeToRefs } from 'pinia'
  import { useSyncFirebaseStore } from '@/app/store/syncFirebase'
  import { useSwipeNavigation } from '@/composables/useSwipeNavigation'
  import { FileDownloadManager } from '@/lib/Task/fileDownloadManager'
  import { startSyncTimer, stopSyncTimer } from '@/app/sync/backgroundSync'
  const { swipeProgress, isSwipeActive, swipeDirection } = useSwipeNavigation()
  const pageTransition = ref('')
  const FullLoaded = ref(false)
  const route = useRoute()
  const appStateStore = useAppStateStore()
  const allTaskStore = useAllTaskStore()
  const userStore = useUserStore()
  const { userCompanyId } = storeToRefs(userStore)
  const syncFirebaseStore = useSyncFirebaseStore()

  const computedUserCompanyId = computed(() => {
    return userCompanyId.value
  })
  const isLoading = computed(() => {
    return appStateStore.isLoading
  })

  const isUniqueLoading = computed(() => {
    return appStateStore.isUniqueLoading
  })

  const loadingPercent = computed(() => {
    return appStateStore.loadingPercent
  })

  const loadingMessage = computed(() => {
    return appStateStore.loadingMessage
  })

  const isTaskLoading = computed(() => {
    return !allTaskStore.isDownloadCompleted
  })

  const checkAndRequestPermissions = async (): Promise<boolean> => {
    return await checkAndRequestAllPermissions()
  }

  // task 경로에서 taskId 추출 (/task/123, /task/123/facility/456 등)
  const extractTaskIdFromPath = (path: string): string | null => {
    const match = path.match(/^\/task\/(\d+)/)
    return match ? match[1] : null
  }

  // companyId 또는 task 페이지 변경 시 sync 리스너 구독
  watch(
    [computedUserCompanyId, (): string => route.path],
    async ([newCompanyId, newPath]: [number | null, string]): Promise<void> => {
      if (!newCompanyId) {
        await syncFirebaseStore.stopSyncListener()
        return
      }
      const taskId = extractTaskIdFromPath(newPath)
      if (taskId) {
        await syncFirebaseStore.initSyncListener(newCompanyId.toString(), taskId)
      } else {
        await syncFirebaseStore.initSyncListener(newCompanyId.toString())
      }
    },
    { immediate: true },
  )

  onMounted(async () => {
    Logger.info('App.vue Mounted')

    // 콜드 시작 시 동기화 타이머 시작 (appStateChange는 상태가 변할 때만 발화)
    startSyncTimer()

    // 앱이 활성화될 때 로그인 상태 확인 후 최신 버전 확인
    CapacitorApp.addListener('appStateChange', async (state: AppState): Promise<void> => {
      if (state.isActive) {
        SQL.markNeedsValidation()
        await getCodepushInstance()?.checkForUpdates()
        startSyncTimer()
      } else {
        // 백그라운드 진입 시 메모리 확보
        FileDownloadManager.getInstance().pauseDownloads()
        stopSyncTimer()
      }
    })

    // 화면 꺼짐(Doze) 감지 — appStateChange가 발생하지 않는 케이스 대응
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        FileDownloadManager.getInstance().pauseDownloads()
        stopSyncTimer()
      } else {
        SQL.markNeedsValidation()
        startSyncTimer()
      }
    })

    // 코드푸시 이벤트 리스너 등록
    if (isApp()) {
      const codepushInstance = getCodepushInstance()
      if (codepushInstance) {
        codepushInstance.on('error', (event: UpdateEvent) => {
          console.error('[codepush] 업데이트 오류:', event.data.error)
          App.toastInstance.add(
            'error',
            '앱 업데이트 중 오류가 발생했습니다. 나중에 다시 시도해주세요.',
          )
          appStateStore.setIsLoading(false)
        })

        codepushInstance.on('downloading', () => {
          console.info('[codepush] 업데이트 다운로드 중...')
          appStateStore.setIsLoading(true)
          App.toastInstance.add(
            'loading',
            '앱 업데이트를 설치하는 중입니다. 잠시 후 앱이 재시작됩니다.',
          )
        })

        codepushInstance.on('reloaded', async () => {
          console.info('[codepush] 업데이트 성공')
          appStateStore.setIsLoading(false)
          App.toastInstance.add('success', '앱 업데이트가 완료되었습니다.')

          // 현재 페이지가 인덱스 페이지인 경우 taskInit 재실행을 위한 이벤트 발생
          if (route.path === '/') {
            // 전역 이벤트 발생
            window.dispatchEvent(new CustomEvent('codepush-success'))
          }

          // 웹에셋 업데이트 완료 후 네이티브 앱 버전 체크
          await checkRequiredNativeUpdate()
        })
      }
    }

    // 이미지·캔버스 롱프레스 메뉴는 막되, 입력 요소는 허용해야 Android WebView가 복사/붙여넣기 툴바를 띄운다
    document.addEventListener('contextmenu', function (event: Event) {
      const target = event.target as Element | null
      if (target?.closest?.('input, textarea, [contenteditable="true"]')) return
      event.preventDefault()
    })

    // SQLite 데이터베이스 초기화 (앱 환경에서만 실행)
    if (isApp()) {
      try {
        await initializeSQLiteDatabase()
      } catch (error) {
        console.error('데이터베이스 초기화 중 오류가 발생했습니다:', error)
      }
    }

    initBackButtonListener()

    // 화면을 먼저 표시하고 초기화 중 로딩 표시
    FullLoaded.value = true
    appStateStore.setIsLoading(true)

    try {
      // App 초기화(인증)와 권한 요청을 병렬 실행
      // getMe()는 authStore에 토큰이 세팅된 후 호출해야 하므로 App.onMounted() 이후 실행
      await Promise.allSettled([
        (async (): Promise<void> => {
          if (App && typeof App.onMounted === 'function') {
            await App.onMounted()
          }

          const currentHref = window.location.href
          const isPublicPath = PUBLIC_PATHS.some(p => currentHref.includes(p))
          if (!userStore.userInfo && !isPublicPath) {
            await userStore
              .getMe()
              .catch(err => console.info('UserStore initialization skipped:', err))
          }
        })(),
        checkAndRequestPermissions(),
      ])
    } finally {
      appStateStore.setIsLoading(false)
    }
  })

  watch(
    () => route.path,
    (_: string, from: string) => {
      if (App && App.previousPath) {
        App.previousPath.value = from
      }
      if (from == '/') {
        return (pageTransition.value = 'slide-up')
      }
    },
  )
</script>
<style scoped lang="scss">
  button {
    padding: 0.6rem 1.2rem;
  }
</style>
<style lang="scss">
  @use '@/assets/css/constant' as *;
  :root {
    --vh: 100vh;
  }
  #app {
    height: var(--vh);
  }
  * {
    /* 모든 요소에서 하이라이트 제거 */
    -webkit-tap-highlight-color: transparent;
    box-sizing: border-box;
    user-select: none;
  }

  html,
  body {
    font-family: var(--font-family);
    margin: 0;
    padding: 0;
  }

  .mobile-only {
    transform: scale(0);
  }

  .app-content {
    background-color: var(--Gray-100);
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    align-items: stretch;
    height: 100%;
    width: 100%;
    padding-top: 0px;
  }

  .app-content.app {
    display: flex;
    justify-content: stretch;
    flex-direction: row;
  }
  .app-content > div {
    flex: 1;
  }

  .component {
    width: 100%;
    height: 100%;
  }

  #modal-popup {
    position: absolute;
  }

  .dev-watermark {
    position: fixed;
    bottom: 25px;
    left: 25px;
    color: rgba(0, 0, 0, 0.4);
    text-align: left;
    pointer-events: none;
    z-index: 99999999999;
    user-select: none;
    font-size: 1.25rem;
    font-weight: normal;
  }

  .dev-watermark-large {
    font-size: 2rem;
    font-weight: bold;
  }

  .dev-route {
    margin-top: 4px;
    font-size: 11px;
    opacity: 0.7;
    word-break: break-all;
    max-width: 280px;
  }

  @media screen and (max-width: 540px) {
    /* 모바일에 사용될 스트일 시트를 여기에 작성합니다. */
    .mobile-only {
      transform: scale(1);
    }
  }
  @media (max-width: $mobile-width) {
    .dev-watermark {
      & > div {
        font-size: 12px;
      }
      .dev-watermark-large {
        font-size: 1.2rem;
      }
    }
  }

  .swipe-nav-indicator {
    position: fixed;
    top: 50%;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999999;
    pointer-events: none;
  }

  .swipe-nav-left {
    left: 6px;
  }

  .swipe-nav-right {
    right: 6px;
  }
</style>
